import { useDispatch, useSelector } from "react-redux";

import { educationEntry, isEducationFirst, readEducationFirst, workHistoryEntry } from "../router/sectionOrder";
import { NavigationFlow, NavigationList } from "../seed/navigation";
//import { NavigationStorage } from "../services/create-resume/resume-flow";

//TODO: revist this to set foundation for template use.
export class NavigationHandler {
	constructor() {
		this.dispatch = useDispatch();
		this.template = useSelector((state) => state.TemplateSelectionReducer);
		this.selectedTemplate = this.template.selectedTemplate;
		this.flow = new NavigationFlow();
		//this.navigationStorage = new NavigationStorage();
	}

	getResumeId() {
		let resumeId = localStorage.getItem("resume_id");

		if (resumeId) {
			return resumeId;
		}

		return "new-resume";
	}

	next(link, skipOne = 0) {
		let navigationFlow = this.getCurrent(this.template.navigationFlow, link);

		this.template.navigationFlow = navigationFlow;

		// The flow is a linked list. Stepping past the last node left `current` null,
		// and reading `.value.url` off it threw. Walk a candidate first and only
		// commit it if it is a real node, so the end of the flow is a no-op rather
		// than a crash that also leaves `current` corrupted.
		let candidate = this.template.navigationFlow.current?.next;
		while (candidate && skipOne > 0) {
			candidate = candidate.next;
			skipOne--;
		}

		if (!candidate?.value?.url) return;

		this.template.navigationFlow.current = candidate;

		localStorage.setItem("currentNav", candidate.value.url);
		this.dispatch({
			type: "CHANGE_TEMPLATE_DETAILS",
			payload: {
				selectedTemplate: {
					yearsOfExperience: this.selectedTemplate.yearsOfExperience,
					templateName: this.selectedTemplate.templateName,
				},
				navigationFlow: this.template.navigationFlow,
			},
		});
	}

	previous(link, skipOne = 0) {
		let navigationFlow = this.getCurrent(this.template.navigationFlow, link);
		this.template.navigationFlow = navigationFlow;

		// Same linked-list guard as next(): stop at the head instead of stepping off it.
		let candidate = this.template.navigationFlow.current?.previous;
		while (candidate && skipOne > 0) {
			candidate = candidate.previous;
			skipOne--;
		}

		if (!candidate?.value?.url) return;

		this.template.navigationFlow.current = candidate;

		localStorage.setItem("currentNav", candidate.value.url);
		this.dispatch({
			type: "CHANGE_TEMPLATE_DETAILS",
			payload: {
				selectedTemplate: {
					yearsOfExperience: this.selectedTemplate.yearsOfExperience,
					templateName: this.selectedTemplate.templateName,
				},
				navigationFlow: this.template.navigationFlow,
			},
		});
	}

	/**
	 * Position the flow on the screen the reader is actually on.
	 *
	 * `link` (the current route segment) wins over the persisted `currentNav`.
	 * The builder also navigates by `<Link>`/URL, which never runs this handler,
	 * so the stored pointer drifts behind the route. When it did, "next" stepped
	 * from the stale node and sent the reader *backwards* — education-summary
	 * went to education-information, and work-history-summary went to
	 * education-summary, so the primary button could never reach Skills.
	 *
	 * Falls back to `currentNav` when the route is not a flow screen (the entry
	 * funnel, say), and leaves the flow untouched when neither resolves.
	 */
	getCurrent(navFlow, link) {
		let yearsOfExperience = localStorage.getItem("yearsOfExperience");
		let templateName = localStorage.getItem("templateName");
		const navigationFlow = new NavigationFlow();

		if (templateName == null || templateName == undefined) {
			templateName = "fresher_template";
			localStorage.setItem("templateName", templateName);
		}

		if (yearsOfExperience == null || yearsOfExperience == undefined) {
			yearsOfExperience = "No Experience";
			localStorage.setItem("yearsOfExperience", yearsOfExperience);
		}

		const isUsable = (url) => url != null && url !== "undefined" && url !== "heading";

		let target = isUsable(link) ? link : localStorage.getItem("currentNav");
		if (!isUsable(target)) {
			target = "contact-information";
		}

		// Already there — keep the existing list so its identity is preserved.
		if (navFlow?.current?.value?.url === target) {
			return navFlow;
		}

		// Pick the flow off the level that was just normalised above, so a reader
		// with experience walks work history before education.
		const flow = navigationFlow.getFlow(isEducationFirst(yearsOfExperience));
		const rebuilt = new NavigationList(flow, flow);

		// Stop at the tail as well as on a match, so a url that is not part of this
		// flow cannot spin forever.
		while (rebuilt.current.value.url !== target && rebuilt.current.next) {
			rebuilt.current = rebuilt.current.next;
		}

		if (rebuilt.current.value.url !== target) {
			// Not a screen in this flow. Leave a positioned list alone rather than
			// dropping the reader at the tail.
			return navFlow?.current ? navFlow : rebuilt;
		}

		localStorage.setItem("currentNav", target);
		return rebuilt;
	}

	getNext(navigationFlow, link, isComplete = false, skipOne = 0) {
		if (isComplete && link !== "education-information" && link !== "work-history" && link !== "work-details") {
			return {
				value: {
					url: "review",
				},
			};
		}
		this.template.navigationFlow = navigationFlow;

		this.next(link, skipOne);
		return this.template.navigationFlow.current;
	}

	//TODO: Let's not hardcode completion status
	getPrevious(navigationFlow, link, isComplete = false, skipOne = 0) {
		if (isComplete) {
			return {
				value: {
					url: "review",
				},
			};
		}

		this.template.navigationFlow = navigationFlow;

		this.previous(link, skipOne);
		return this.template.navigationFlow.current;
	}

	getFirstIncompleteScreen(resumeObject) {
		const resumeProgress = resumeObject.journal.progressStatus;
		const { education, experience } = resumeObject;

		// Walk the sections in the order this reader is asked for them, so resuming
		// an unfinished resume drops them where the rail says they left off.
		const educationStep = { status: resumeProgress.EDUCATION, url: educationEntry(education.length) };
		const workHistoryStep = { status: resumeProgress.EXPERIENCE, url: workHistoryEntry(experience.length) };

		const steps = [
			{ status: resumeProgress.HEADING, url: "contact-information" },
			...(readEducationFirst() ? [educationStep, workHistoryStep] : [workHistoryStep, educationStep]),
			{ status: resumeProgress.SKILLS, url: "skills" },
			{ status: resumeProgress.SUMMARY, url: "brief-summary" },
			// Everything before this is done, so there is nothing left to fill in.
			{ status: undefined, url: "review" },
		];

		return steps.find((step) => step.status !== "COMPLETED").url;
	}

	setFinalize(backLink) {
		backLink = backLink == "extras" ? "brief-summary" : backLink;
		let likArray = [
			"contact-information",
			"work-history",
			"education-summary",
			"skills",
			"work-details",
			"work-history-summary",
			"education-information",
			"education-summary",
			"brief-summary",
			"review",
		];
		if (!likArray.includes(backLink)) return;

		this.template.navigationFlow.current = undefined;
		localStorage.setItem("currentNav", backLink);
		let navigationFlow = this.getCurrent(this.template.navigationFlow, backLink);
		this.template.navigationFlow = navigationFlow;
		localStorage.setItem("currentNav", this.template.navigationFlow.current.value.url);
		this.dispatch({
			type: "CHANGE_TEMPLATE_DETAILS",
			payload: {
				selectedTemplate: {
					yearsOfExperience: this.selectedTemplate.yearsOfExperience,
					templateName: this.selectedTemplate.templateName,
				},
				navigationFlow: this.template.navigationFlow,
			},
		});
	}
}
